/**
 * 歌词/标题与语言相关的纯函数（抽离自 MusicPlayer.js）
 */
import { ZH_CN, ZH_TW } from '../constants';
import { normalizeInstrumentalTitle } from '../../../utils/musicMeta';

/**
 * 根据当前界面语言生成默认翻译映射
 * @param {object|null} savedSettings - loadPlayerSettings() 结果
 * @param {string} currentLangForInit - 界面语言 ja/en/cn/tw
 * @returns {object} translationMap
 */
export function getDefaultTranslationMap(savedSettings, currentLangForInit) {
  if (savedSettings?.lyricSettings?.translationMap) {
    return savedSettings.lyricSettings.translationMap;
  }

  let defaultTarget = 'zh';
  if (currentLangForInit === 'ja') defaultTarget = 'ja';
  else if (currentLangForInit === 'en') defaultTarget = 'en';

  return {
    zh: (currentLangForInit === 'cn' || currentLangForInit === 'tw') ? 'ja' : defaultTarget,
    ja: currentLangForInit === 'ja' ? 'zh' : defaultTarget,
    en: currentLangForInit === 'en' ? 'zh' : defaultTarget,
    other: defaultTarget
  };
}

/**
 * 根据歌曲语言与翻译设置解析目标翻译语言码
 * @param {object|null} music
 * @param {object} translationMap - lyricSettings.translationMap（或 lyricSettingsRef.current?.translationMap）
 * @param {string} currentLang - 站内 UI 语言 cn/tw/ja/en
 * @returns {string} 'ja' | 'en' | ZH_CN | ZH_TW
 */
export function resolveTargetTranslationLang(music, translationMap, currentLang) {
  try {
    const src = String(music?.language_category || '').trim();
    const map = translationMap || {};
    let group = 'other';
    if (src === 'cn' || src === 'tw' || src === ZH_CN || src === ZH_TW) group = 'zh';
    else if (src === 'ja') group = 'ja';
    else if (src === 'en') group = 'en';

    const chosen = map[group] || (group === 'zh' ? 'ja' : 'zh');
    if (chosen === 'ja') return 'ja';
    if (chosen === 'en') return 'en';
    return String(currentLang || '') === 'tw' ? ZH_TW : ZH_CN;
  } catch (_) {
    return String(currentLang || '') === 'tw' ? ZH_TW : ZH_CN;
  }
}

/**
 * 根据当前界面语言取音乐标题（含乐器曲 normalize）
 * @param {object|null} music
 * @param {string} currentLang - cn/tw/ja/en
 * @returns {string}
 */
export function getCurrentLanguageTitle(music, currentLang) {
  if (!music) return '';
  const title = (() => {
    switch (currentLang) {
      case 'cn':
        return music.title_zh_cn || music.title;
      case 'tw':
        return music.title_zh_tw || music.title;
      case 'ja':
        return music.title_ja || music.title;
      case 'en':
        return music.title_en || music.title;
      default:
        return music.title;
    }
  })();
  return normalizeInstrumentalTitle(music, title);
}
